import { clsx } from 'clsx';
import { ReactNode } from 'react';
import { X } from 'lucide-react';

interface BadgeProps {
  children: ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  size?: 'sm' | 'md';
  className?: string;
  dot?: boolean;
  onDismiss?: () => void;
}

export function Badge({
  children,
  variant = 'default',
  size = 'sm',
  className,
  dot = false,
  onDismiss,
}: BadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full font-medium',
        // Variants with dark mode
        variant === 'default' && 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
        variant === 'success' && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        variant === 'warning' && 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        variant === 'error' && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        variant === 'info' && 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        // Sizes
        size === 'sm' && 'px-2 py-0.5 text-xs',
        size === 'md' && 'px-2.5 py-1 text-sm',
        className
      )}
    >
      {dot && (
        <span
          className={clsx(
            'w-1.5 h-1.5 rounded-full mr-1.5',
            variant === 'default' && 'bg-gray-500 dark:bg-gray-400',
            variant === 'success' && 'bg-green-500 dark:bg-green-400',
            variant === 'warning' && 'bg-yellow-500 dark:bg-yellow-400',
            variant === 'error' && 'bg-red-500 dark:bg-red-400',
            variant === 'info' && 'bg-blue-500 dark:bg-blue-400'
          )}
        />
      )}
      {children}
      {onDismiss && (
        <button
          onClick={onDismiss}
          className={clsx(
            'ml-1 -mr-0.5 p-0.5 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors',
            'focus:outline-none focus:ring-1 focus:ring-offset-1',
            variant === 'default' && 'focus:ring-gray-400',
            variant === 'success' && 'focus:ring-green-400',
            variant === 'warning' && 'focus:ring-yellow-400',
            variant === 'error' && 'focus:ring-red-400',
            variant === 'info' && 'focus:ring-blue-400'
          )}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

// Status badge with pulsing dot
interface StatusBadgeProps {
  status: 'online' | 'offline' | 'pending' | 'error';
  label?: string;
  className?: string;
}

export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  const config = {
    online: {
      label: label || 'Online',
      dotClass: 'bg-green-500',
      textClass: 'text-green-700 dark:text-green-400',
      bgClass: 'bg-green-100 dark:bg-green-900/30',
      pulse: true,
    },
    offline: {
      label: label || 'Offline',
      dotClass: 'bg-gray-400',
      textClass: 'text-gray-600 dark:text-gray-400',
      bgClass: 'bg-gray-100 dark:bg-gray-700',
      pulse: false,
    },
    pending: {
      label: label || 'Pending',
      dotClass: 'bg-yellow-500',
      textClass: 'text-yellow-700 dark:text-yellow-400',
      bgClass: 'bg-yellow-100 dark:bg-yellow-900/30',
      pulse: true,
    },
    error: {
      label: label || 'Error',
      dotClass: 'bg-red-500',
      textClass: 'text-red-700 dark:text-red-400',
      bgClass: 'bg-red-100 dark:bg-red-900/30',
      pulse: false,
    },
  };

  const { label: statusLabel, dotClass, textClass, bgClass, pulse } = config[status];

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        bgClass,
        textClass,
        className
      )}
    >
      <span className="relative flex h-2 w-2">
        {pulse && (
          <span
            className={clsx(
              'absolute inline-flex h-full w-full animate-ping rounded-full opacity-75',
              dotClass
            )}
          />
        )}
        <span className={clsx('relative inline-flex h-2 w-2 rounded-full', dotClass)} />
      </span>
      {statusLabel}
    </span>
  );
}

interface NodeTypeBadgeProps {
  type: 'gateway' | 'compute' | 'storage' | 'daq';
  className?: string;
}

export function NodeTypeBadge({ type, className }: NodeTypeBadgeProps) {
  const labels: Record<string, string> = {
    gateway: 'Gateway',
    compute: 'Compute',
    storage: 'Storage',
    daq: 'DAQ',
  };

  const colors: Record<string, string> = {
    gateway: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    compute: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    storage: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    daq: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        colors[type],
        className
      )}
    >
      {labels[type]}
    </span>
  );
}
